import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {NOTIFICATION_TIMEOUTS} from 'src/constants';
import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {useVendor} from 'src/hooks/use-vendor';
import {useVendors} from 'src/hooks/use-vendors';
import {
    ALLOWED_VENDOR_NAME_REGEX,
    MIN_VENDOR_NAME_LENGTH,
    normalizeVendorName,
    sanitizeVendorName,
} from 'src/modules/vendor/shared/vendorValidation';
import {useVendorRules} from 'src/modules/vendor-profile/hooks/use-vendor-rules';
import {deleteVendor, updateVendor} from 'src/openapi/admin/apis/VendorManagementApi';

import {useShown} from '@pb/kit-hooks-use-shown';

import {AssignedUsersSection} from './components/AssignedUsersSection';
import {DeleteVendorDialog} from './components/delete-vendor-dialog';
import {RulesSection} from './components/RulesSection';
import {VendorDetails} from './components/vendor-details';
import {VendorProfileHeader} from './components/vendor-profile-header';
import {VendorProfileSkeleton} from './components/vendor-profile-skeleton';

import {container} from './vendor-profile.module.styl';

type Params = {
    vendorId?: string,
};

export const VendorProfile: React.FC = () => {
    const {vendorId} = useParams<Params>();
    const navigate = useNavigate();
    const {vendor, loading, reload: reloadVendor} = useVendor(vendorId);
    const {vendors} = useVendors();
    const {executeWithNotifications} = useApiWithNotifications();
    const vendorRules = useVendorRules(vendor?.id);
    const {shown: deleteDialogShown, onToggle: toggleDeleteDialog, onClose: closeDeleteDialog} = useShown();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);

    useEffect(() => {
        if (vendor) {
            setName(vendor.vendorName);
            setDescription(vendor.description ?? '');
        }
    }, [vendor]);

    const normalizedName = useMemo(() => normalizeVendorName(name), [name]);
    const duplicateName = useMemo(() => {
        if (!normalizedName || !vendorId) {
            return false;
        }
        return vendors.some(existing => (
            existing.id !== vendorId
            && normalizeVendorName(existing.vendorName) === normalizedName
        ));
    }, [normalizedName, vendorId, vendors]);

    const nameError = useMemo(() => {
        if (!normalizedName) {
            return 'Vendor name is required';
        }
        if (name.trim().length < MIN_VENDOR_NAME_LENGTH) {
            return `Name must be at least ${MIN_VENDOR_NAME_LENGTH} characters`;
        }
        if (!ALLOWED_VENDOR_NAME_REGEX.test(name.trim())) {
            return 'Name contains unsupported characters';
        }
        if (duplicateName) {
            return 'A vendor with this name already exists';
        }
        return null;
    }, [duplicateName, name, normalizedName]);

    const hasRules = useMemo(() => vendorRules.rules.length > 0, [vendorRules.rules]);

    const deleteDisabled = useMemo(() => (
        !vendor
        || loading
        || vendorRules.loadingRules
        || Boolean(vendorRules.error)
        || hasRules
        || deleteBusy
    ), [deleteBusy, hasRules, loading, vendor, vendorRules.error, vendorRules.loadingRules]);

    const deleteTooltip = useMemo(() => (
        hasRules ? 'To delete this vendor, first remove all rules associated with it.' : undefined
    ), [hasRules]);

    const isDirty = useMemo(() => {
        if (!vendor) {
            return false;
        }
        const initialDescription = vendor.description ?? '';
        return (
            name.trim() !== vendor.vendorName
            || description.trim() !== initialDescription
        );
    }, [description, name, vendor]);

    const canSave = Boolean(isDirty && !nameError && !loading && vendor && !saving);

    const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setName(sanitizeVendorName(value));
    }, []);

    const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.currentTarget.value);
    }, []);

    const handleSave = useCallback(async () => {
        if (!vendor || !isDirty || nameError) {
            return;
        }

        setSaving(true);

        try {
            const response = await executeWithNotifications(
                updateVendor({
                    id: vendor.id,
                    updateVendorRequest: {
                        vendorName: name.trim(),
                        description: description.trim() || undefined,
                    },
                }),
                {
                    success: 'Vendor successfully updated.',
                    error: 'Unable to update vendor. Please try again.',
                    exception: 'Unable to update vendor. Please try again.',
                },
                'update-vendor',
                NOTIFICATION_TIMEOUTS,
            );

            if (response.success) {
                await Promise.all([reloadVendor()]);
            }
        } finally {
            setSaving(false);
        }
    }, [description, executeWithNotifications, isDirty, name, nameError, reloadVendor, vendor]);

    const handleOpenDeleteDialog = useCallback(() => {
        toggleDeleteDialog();
    }, [toggleDeleteDialog]);

    const handleCloseDeleteDialog = useCallback(() => {
        if (!deleteBusy) {
            closeDeleteDialog();
        }
    }, [closeDeleteDialog, deleteBusy]);

    const handleConfirmDelete = useCallback(async () => {
        if (!vendor) {
            return;
        }

        setDeleteBusy(true);

        try {
            const response = await executeWithNotifications(
                deleteVendor({id: vendor.id}),
                {
                    success: 'Vendor successfully deleted.',
                    error: 'Unable to delete vendor. Please try again.',
                    exception: 'Unable to delete vendor. Please try again.',
                },
                'delete-vendor',
                NOTIFICATION_TIMEOUTS,
            );

            if (response.success) {
                navigate('/vendors');
            }
        } catch (err) {
            console.error('Failed to delete vendor:', err);
        } finally {
            setDeleteBusy(false);
            closeDeleteDialog();
        }
    }, [closeDeleteDialog, executeWithNotifications, navigate, vendor]);

    return (
        <div className={container}>
            <VendorProfileHeader
                vendorDisplayName={vendor?.vendorName}
                isLoadingName={!vendor}
                canSave={canSave}
                onSave={handleSave}
                onDelete={handleOpenDeleteDialog}
                deleteDisabled={deleteDisabled}
                deleteTooltip={deleteTooltip}
            />
            {loading ? (
                <VendorProfileSkeleton/>
            ) : (
                <VendorDetails
                    name={name}
                    description={description}
                    onNameChange={handleNameChange}
                    onDescriptionChange={handleDescriptionChange}
                />
            )}
            <RulesSection vendor={vendor} vendorRules={vendorRules}/>
            <AssignedUsersSection vendor={vendor}/>
            <DeleteVendorDialog
                shown={deleteDialogShown}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleConfirmDelete}
                busy={deleteBusy}
            />
        </div>
    );
};
